/* eslint-disable react/require-default-props */
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { toast } from 'react-toastify';

import Modal from '../../../../../components/Modal/Modal';
import { PrimaryButton, GhostButton } from '../../../../../styles/ui/Button';
import { updateInterview } from '../../../../../services/hr/recruitment/interviewsApi';
import { defaultModalityForRound } from '../../constants';
import { toIsoWithOffset, toDateTimeLocalValue } from '../../utils/format';
import { readApiError, recruitmentErrorMessage } from '../../utils/apiError';
import InterviewScheduleFields from './InterviewScheduleFields';

// ---------------------------------------------------------------------------
// Editar a entrevista ativa ANTES do resultado (mesma entrevista — PATCH
// /interviews/:id). Não gera histórico (isso é a REMARCAÇÃO). Pré-preenche com
// os dados atuais.
// ---------------------------------------------------------------------------

export default function EditInterviewModal({
   isOpen,
   onClose,
   interview,
   onSaved,
}) {
   const [modality, setModality] = useState(defaultModalityForRound());
   const [interviewerId, setInterviewerId] = useState('');
   const [scheduledAt, setScheduledAt] = useState('');
   const [errors, setErrors] = useState({});
   const [isSubmitting, setIsSubmitting] = useState(false);

   useEffect(() => {
      if (isOpen && interview) {
         setModality(
            interview.modality ?? defaultModalityForRound(interview.round),
         );
         setInterviewerId(
            interview.interviewer_user_id
               ? String(interview.interviewer_user_id)
               : '',
         );
         setScheduledAt(toDateTimeLocalValue(interview.scheduled_at));
         setErrors({});
         setIsSubmitting(false);
      }
   }, [isOpen, interview]);

   const handleConfirm = async () => {
      if (isSubmitting) return;
      if (!scheduledAt) {
         setErrors({ scheduled_at: 'Informe a data e a hora.' });
         return;
      }

      setIsSubmitting(true);
      setErrors({});
      try {
         const { message } = await updateInterview(interview.id, {
            modality,
            interviewerUserId: interviewerId ? Number(interviewerId) : null,
            scheduledAt: toIsoWithOffset(scheduledAt),
         });
         toast.success(message);
         onSaved?.();
         onClose?.();
      } catch (err) {
         const { fieldErrors } = readApiError(err);
         if (Object.keys(fieldErrors).length > 0) {
            setErrors(fieldErrors);
         } else {
            toast.error(
               recruitmentErrorMessage(err, 'Erro ao editar a entrevista.'),
            );
         }
      } finally {
         setIsSubmitting(false);
      }
   };

   const footer = (
      <>
         <GhostButton type="button" onClick={onClose} disabled={isSubmitting}>
            Cancelar
         </GhostButton>
         <PrimaryButton
            type="button"
            onClick={handleConfirm}
            disabled={isSubmitting}
         >
            {isSubmitting ? 'Salvando…' : 'Salvar alterações'}
         </PrimaryButton>
      </>
   );

   return (
      <Modal
         isOpen={isOpen}
         onClose={onClose}
         title={`Editar Entrevista ${interview?.round ?? ''}`}
         maxWidth="560px"
         footer={footer}
      >
         <InterviewScheduleFields
            modality={modality}
            onModality={setModality}
            interviewerId={interviewerId}
            onInterviewer={setInterviewerId}
            scheduledAt={scheduledAt}
            onScheduledAt={setScheduledAt}
            disabled={isSubmitting}
            errors={errors}
         />
      </Modal>
   );
}

EditInterviewModal.propTypes = {
   isOpen: PropTypes.bool.isRequired,
   onClose: PropTypes.func.isRequired,
   interview: PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      round: PropTypes.number,
      modality: PropTypes.string,
      interviewer_user_id: PropTypes.oneOfType([
         PropTypes.string,
         PropTypes.number,
      ]),
      interviewer: PropTypes.shape({ name: PropTypes.string }),
      scheduled_at: PropTypes.string,
   }),
   onSaved: PropTypes.func,
};
